import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, Trash2, GripVertical, ExternalLink 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { CatalogItem } from '@/features/catalog/types';
import type { RecipeIngredient } from '../../types';
import type { RecipeFormData } from './form-types';
import { UNITS } from './form-constants';
import { CatalogItemPicker } from './CatalogItemPicker';

interface IngredientsTabProps {
  readonly formData: RecipeFormData;
  readonly updateField: <K extends keyof RecipeFormData>(field: K, value: RecipeFormData[K]) => void;
}

export function IngredientsTab({
  formData, updateField 
}: IngredientsTabProps) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const handleSelectCatalogItem = (item: CatalogItem) => {
    const newIngredient: RecipeIngredient = {
      ingredientId: `ing-${Date.now()}`,
      itemId: item.itemId,
      itemName: item.name,
      quantity: 0,
      unit: item.unit,
      prepNotes: '',
      isOptional: false,
    };
    updateField('ingredients', [...formData.ingredients, newIngredient]);
  };

  const updateIngredient = (index: number, updates: Partial<RecipeIngredient>) => {
    const newIngredients = [...formData.ingredients];
    newIngredients[index] = {
      ...newIngredients[index],
      ...updates 
    };
    updateField('ingredients', newIngredients);
  };

  const removeIngredient = (index: number) => {
    updateField('ingredients', formData.ingredients.filter((_, i) => i !== index));
  };

  const existingItemIds = formData.ingredients.map((ing) => ing.itemId).filter(Boolean);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Add ingredients from the{' '}
          <Link to="/catalog/items" target="_blank" className="text-primary hover:underline inline-flex items-center gap-1">
            catalog
            <ExternalLink className="h-3 w-3" />
          </Link>
          . Each ingredient links to the catalog item.
        </p>
        <Button onClick={() => setIsPickerOpen(true)} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add Ingredient
        </Button>
      </div>

      {formData.ingredients.length === 0 && (
        <div className="rounded-lg border-2 border-dashed p-8 text-center">
          <p className="text-muted-foreground">No ingredients added yet</p>
          <Button onClick={() => setIsPickerOpen(true)} variant="outline" className="mt-4">
            <Plus className="mr-2 h-4 w-4" />
            Add First Ingredient
          </Button>
        </div>
      )}

      {formData.ingredients.length > 0 && (
        <div className="space-y-3">
          {formData.ingredients.map((ingredient, index) => (
            <div
              key={ingredient.ingredientId}
              className="flex items-start gap-3 rounded-lg border p-3"
            >
              <div className="flex-shrink-0 pt-2 cursor-grab text-muted-foreground">
                <GripVertical className="h-4 w-4" />
              </div>
              <div className="flex-1 grid gap-3 md:grid-cols-12">
                <div className="md:col-span-4 space-y-1">
                  <label className="text-xs text-muted-foreground">Item Name</label>
                  <div className="flex items-center gap-1">
                    {ingredient.itemId ? (
                      <Link
                        to={`/catalog/items?search=${encodeURIComponent(ingredient.itemName)}`}
                        target="_blank"
                        className="w-full rounded-md border bg-muted/30 px-2 py-1.5 text-sm text-primary hover:underline flex items-center justify-between"
                      >
                        <span className="truncate">{ingredient.itemName}</span>
                        <ExternalLink className="h-3 w-3 flex-shrink-0" />
                      </Link>
                    ) : (
                      <input
                        type="text"
                        value={ingredient.itemName}
                        onChange={(e) => updateIngredient(index, { itemName: e.target.value })}
                        placeholder="e.g., Salmon Fillet"
                        className="w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    )}
                  </div>
                </div>
                <div className="md:col-span-2 space-y-1">
                  <label className="text-xs text-muted-foreground">Quantity</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={ingredient.quantity || ''}
                    onChange={(e) => updateIngredient(index, { quantity: parseFloat(e.target.value) || 0 })}
                    placeholder="200"
                    className="w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div className="md:col-span-2 space-y-1">
                  <label className="text-xs text-muted-foreground">Unit</label>
                  <select
                    value={ingredient.unit}
                    onChange={(e) => updateIngredient(index, { unit: e.target.value })}
                    className="w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {UNITS.map((u) => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-3 space-y-1">
                  <label className="text-xs text-muted-foreground">Prep Notes</label>
                  <input
                    type="text"
                    value={ingredient.prepNotes ?? ''}
                    onChange={(e) => updateIngredient(index, { prepNotes: e.target.value })}
                    placeholder="diced, minced..."
                    className="w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div className="md:col-span-1 flex items-end justify-center pb-1">
                  <label className="flex items-center gap-1 text-xs">
                    <input
                      type="checkbox"
                      checked={ingredient.isOptional ?? false}
                      onChange={(e) => updateIngredient(index, { isOptional: e.target.checked })}
                      className="rounded"
                    />
                    Opt
                  </label>
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeIngredient(index)}
                className="flex-shrink-0 p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <CatalogItemPicker
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        onSelect={handleSelectCatalogItem}
        excludeItemIds={existingItemIds}
      />
    </div>
  );
}
